import classNames from 'classnames'
import { useMemo, useState } from 'react'

import { RadionetLogo, RadionetLogoOnly } from '@apps-common/components/common/logos'
import { ButtonRoundedWithIcon } from '@apps-common/components/elements/button'
import { ImageSelect, InputInfoSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerXl } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { DEFAULT_PALETTES, ThemeMode } from '@apps-common/config/theme'
import { useTheme } from '@apps-common/hooks/useTheme'
import { useThemeStore } from '@apps-common/stores/useThemeStore'
import type { ICompanyListModel } from '@apps-common/types/management/company'
import type { PortalThemeModel, ThemePalette, ThemePayload } from '@apps-common/types/portal/theme'

interface IProps {
  itemSelected: (key: string, color: string) => void
  companiesData: any
  createOrUpdateData: any
}

type PaletteMode = 'light' | 'dark'

const PALETTE_COLOR_KEYS: (keyof ThemePalette)[] = [
  'brand',
  'onBrand',
  'bg',
  'surface',
  'surface2',
  'border',
  'text',
  'textMuted',
  'success',
  'onSuccess',
  'warning',
  'onWarning',
  'error',
  'onError',
  'info',
  'onInfo',
  'focus',
  'disabledBg',
  'disabledText',
  'myMonitoringBrand',
  'onMyMonitoringBrand',
  'myMonitoringBrand2',
  'onMyMonitoringBrand2',
  'myMonitoringLogoBg',
  'logoBg'
]

const labelFor = (k: string) =>
  k
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (m) => m.toUpperCase())
    .replace('Bg', 'BG')

export const ThemeManagementForm: React.FC<IProps> = ({ itemSelected, companiesData, createOrUpdateData }) => {
  const { globalThemeMode, refetch, companyId, changeCompanyId, contextTheme } = useTheme()

  const { theme, modifiedTheme, setModifiedPaletteValue } = useThemeStore()
  const [files, setFiles] = useState<{ key: string; file: any }[]>([])

  const { data: companies, isLoading: companiesIsLoading, isError: companiesIsError } = companiesData({})
  const { mutate: createOrUpdateTheme, isPending: createOrUpdateThemeIsPending } = createOrUpdateData(true, () => refetch())

  const loading = companiesIsLoading || companiesIsError
  const updating = createOrUpdateThemeIsPending

  const paletteMode: PaletteMode = globalThemeMode === ThemeMode.dark ? 'dark' : 'light'

  // Prefer modifiedTheme, but fall back to stored theme
  const baseTheme = useMemo(() => {
    return (modifiedTheme || contextTheme || theme) as PortalThemeModel | undefined
  }, [modifiedTheme, contextTheme, theme])

  const parsedPalettes = useMemo(() => {
    const t: any = baseTheme
    if (!t) return null

    if (t.palettes) return t.palettes
    if (t.palettesJson) {
      try {
        return JSON.parse(t.palettesJson)
      } catch {
        return null
      }
    }

    // extra safety for backend naming differences
    if (t.palettesJSON) {
      try {
        return JSON.parse(t.palettesJSON)
      } catch {
        return null
      }
    }

    return null
  }, [baseTheme])

  const palette: ThemePalette = useMemo(() => {
    const fallback = paletteMode === 'dark' ? DEFAULT_PALETTES.dark : DEFAULT_PALETTES.light

    return {
      ...fallback,
      ...(parsedPalettes?.[paletteMode] ?? {})
    }
  }, [parsedPalettes, paletteMode])

  const upsertFile = (key: string, file: File | null) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.key !== key)
      if (file) next.push({ key, file })
      return next
    })
  }

  const clearLogo = (logoType: 'primary' | 'secondary') => {
    if (!modifiedTheme) return

    if (paletteMode === 'light') {
      if (logoType === 'primary') upsertFile('logoPrimaryLight', null)
      if (logoType === 'secondary') upsertFile('logoSecondaryLight', null)
    } else {
      if (logoType === 'primary') upsertFile('logoPrimaryDark', null)
      if (logoType === 'secondary') upsertFile('logoSecondaryDark', null)
    }

    const paletteKey = logoType === 'primary' ? 'logoPrimary' : 'logoSecondary'
    setModifiedPaletteValue(paletteMode, paletteKey, '')
  }

  const addLogoFile = (logoType: 'primary' | 'secondary', file: File) => {
    if (!file) return

    // FormData keys must match controller parameters
    const formKey =
      paletteMode === 'light'
        ? logoType === 'primary'
          ? 'logoPrimaryLight'
          : 'logoSecondaryLight'
        : logoType === 'primary'
          ? 'logoPrimaryDark'
          : 'logoSecondaryDark'

    upsertFile(formKey, file)

    // Optimistic preview in the editor (blob URL)
    const paletteKey = logoType === 'primary' ? 'logoPrimary' : 'logoSecondary'
    setModifiedPaletteValue(paletteMode, paletteKey, URL.createObjectURL((file as any)?.blob || file))
  }

  const onSubmit = () => {
    if (!baseTheme) return

    const t: any = baseTheme

    // Build a full payload of BOTH palettes (light + dark) from the latest source we have.
    const palettes: ThemePayload = {
      light: {
        ...DEFAULT_PALETTES.light,
        ...(t.palettes?.light ?? parsedPalettes?.light ?? {})
      },
      dark: {
        ...DEFAULT_PALETTES.dark,
        ...(t.palettes?.dark ?? parsedPalettes?.dark ?? {})
      }
    }

    // IMPORTANT: force palettesJson to match what we’re sending
    const modelToSend = {
      ...t,
      companyId,
      palettes,
      palettesJson: JSON.stringify(palettes)
    }

    createOrUpdateTheme({ model: modelToSend, files })
  }

  return (
    <div className='pt-[120px] overflow-y-scroll h-[calc(100vh)] scrollbar-none w-[90%]'>
      <div className='px-3 py-2 rounded-2xl my-4 flex items-center justify-between gap-[5px] text-[0.8rem] jost-medium bg-info/50 text-on-info border border-info'>
        Editing {globalThemeMode} theme <commonIcons.themeLight className='text-[1rem]' />
      </div>

      <SelectInputSM
        id='company'
        showLabel
        label='Company'
        placeholder='Select company'
        loading={loading}
        disabled={updating}
        options={companies?.map((c: ICompanyListModel) => ({ label: c.companyName, value: c.companyId })) || []}
        registration={{
          value: companyId || '',
          onChange: (e: any) => {
            const value = e?.target?.value
            if (value) changeCompanyId(value)
          }
        }}
      />

      {/* Logos */}
      <InputInfoSM
        id={`${paletteMode}-logoPrimary`}
        hideNoEditIcon
        showLabel
        label={`${labelFor(paletteMode)} Logo Primary`}
        className='flex items-center justify-center !pb-[15px] !pt-[18px] !bg-logo-background'
        value={
          <ImageSelect
            existingImageUrl={palette.logoPrimary || undefined}
            containerClasses='w-full'
            existingClasses='max-h-[25px] ml-auto !text-text opacity-100'
            onChangeCallback={(file: any) => addLogoFile('primary', file)}
            onClearCallback={() => {}}
            disabled={loading || updating}
            clearChildren={
              <button
                type='button'
                aria-label='clear-logo-primary'
                className='absolute top-[-6px] right-[-15px] bg-surface text-text rounded-full'
                onClick={() => clearLogo('primary')}
              >
                <commonIcons.close />
              </button>
            }
          >
            <RadionetLogo color='fill-text' classes='w-[140px] h-[24px]' />
          </ImageSelect>
        }
      />

      <InputInfoSM
        id={`${paletteMode}-logoSecondary`}
        hideNoEditIcon
        showLabel
        label={`${labelFor(paletteMode)} Logo Secondary`}
        className='flex items-center justify-center !pb-[15px] !pt-[18px] !bg-logo-background'
        value={
          <ImageSelect
            existingImageUrl={palette.logoSecondary || undefined}
            containerClasses='w-full'
            existingClasses='max-h-[25px] ml-auto !text-text opacity-100'
            onChangeCallback={(file: any) => addLogoFile('secondary', file)}
            onClearCallback={() => {}}
            disabled={loading || updating}
            clearChildren={
              <button
                type='button'
                aria-label='clear-logo-secondary'
                className='absolute top-[-6px] right-[-15px] bg-surface text-text rounded-full'
                onClick={() => clearLogo('secondary')}
              >
                <commonIcons.close />
              </button>
            }
          >
            <RadionetLogoOnly color='fill-text' classes='w-[45px] h-[40px]' />
          </ImageSelect>
        }
      />

      {/* Color palette fields */}
      {PALETTE_COLOR_KEYS.map((k, i) => {
        const value = (palette as any)[k] as string | undefined
        if (!value) return null

        const keyPath = `${paletteMode}.${String(k)}`
        const label = labelFor(String(k))

        return (
          <InputInfoSM
            key={`theme-input-${keyPath}-${i}`}
            id={keyPath}
            hideNoEditIcon
            label={label}
            value={
              <div className={classNames('text-text text-[0.9em] flex items-center justify-between')}>
                {label}
                <button type='button' aria-label='Change color' disabled={loading || updating} onClick={() => itemSelected(keyPath, value)}>
                  <div style={{ backgroundColor: value }} className='w-[25px] h-[25px] border border-border rounded-full' />
                </button>
              </div>
            }
          />
        )
      })}

      <VertSpacerLg />

      <ButtonRoundedWithIcon
        type='button'
        text='Update Theme'
        icon={<commonIcons.arrowAngled />}
        disabled={loading || updating}
        className='action-brand-solid w-full'
        iconContainerClassName='bg-on-brand text-brand'
        callback={() => onSubmit()}
      />

      <VertSpacerXl />
    </div>
  )
}
